import React, { useEffect, useRef, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faCircleInfo, faLock } from "@fortawesome/free-solid-svg-icons";
import Input from "../../Elements/Input";
import Button from "../../Elements/Button";
import Switch from "../../Elements/Form/Switch";
import LanguageSelect from "../../CommonComponents/LanguageSelect";
import "../../Pages/Kdf/EditKdf/editorHeader/viewElements/SettingsEnableOPTIN.css";
import CustomTooltip from "../../Elements/Tooltip";
import { networkService } from "../../config/axios";
import { csrfToken } from "../../../utils/csrfTokenUtils";
import { resolvePrivacyUrl } from "../../../utils/optInGate";
import { DEFAULT_OPTIN_AFTER_PAGES, OPTIN_MODES } from "../../../constants/optInDefaults";
import styles from "./links.module.css";
import "./PersonalInsightsOptinSettings.css";

const DELIVERS_OPTIONS = [
  { value: "guide", label: "Personal Insights (guide by email)", help: "An email built from the pages they viewed and the answers they chose." },
  { value: "document", label: "PDF Delivery (via email)", help: "An email with a secure link to the PDF you upload." },
  { value: "reference", label: "Reference number", help: "An email with a reference number. No guide and no file." },
];

const VIEWED_PAGE_OPTIONS = [2, 3, 4, 5, 6];

const ORDINALS = { 2: "2nd", 3: "3rd", 4: "4th", 5: "5th", 6: "6th" };

const COVERAGE_HELP = {
  problem: { label: "Problem", example: "What is the main challenge you want to solve?" },
  size: { label: "Size", example: "Which best describes your team today?" },
  urgency: { label: "Urgency", example: "When do you need this in place?" },
  constraints: { label: "Constraints", example: "What must any solution respect (budget, systems, rules)?" },
};

const GUIDANCE_TIPS = [
  "Say what the recipient is trying to solve, in their words, and what a good outcome looks like for them.",
  "List claims and promises the guide must not make, such as prices, deadlines or guarantees.",
  "Name the questions a buyer should ask any vendor in your category, so the guide asks them honestly.",
  "Tell the AI who the reader is: a decision maker, an evaluator, or someone who must convince others.",
  "Keep it short. A few clear sentences work better than a long brief.",
];

const DOCUMENT_STATUS = {
  quarantined: "Checking for viruses…",
  clean: "Ready to send",
  rejected: "Rejected",
  disabled: "Disabled by Kinetizine",
};

const variantFor = (delivers) => (delivers === "guide_auto" || delivers === "guide_review" ? "guide" : delivers);

const formatSize = (bytes) => (bytes ? `${(Number(bytes) / 1024 / 1024).toFixed(1)} MB` : "");

const joinWithAnd = (items) =>
  items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

const hasValue = (v) => v !== "" && v !== null && v !== undefined;

const PersonalInsightsOptinSettings = ({
  editLaserLinkItem,
  updateEditLaserLinkItem,
  profile,
  fillPreview,
  ownerEmail,
  documentError,
  triggerPageError,
  afterPagesError,
  totalPages,
  accessControls,
}) => {
  const copy = profile?.personal_insights_copy || {};
  const delivers = editLaserLinkItem?.optin_delivers || "guide_auto";
  const variant = variantFor(delivers);
  const optIn = editLaserLinkItem?.opt_in || {};
  const optinEnabled = !!editLaserLinkItem?.has_optin;
  const guideNames = editLaserLinkItem?.recipient_guide_names || {};
  const [showAllNames, setShowAllNames] = useState(false);
  const [translatingName, setTranslatingName] = useState(false);
  const translatedNameRef = useRef(guideNames.en?.trim() || "");
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [previewLocale, setPreviewLocale] = useState("en");
  const [coverage, setCoverage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);
  const hostedDocument = editLaserLinkItem?.hosted_document;
  const trigger = editLaserLinkItem?.guide_trigger === "page" ? "page" : "end_of_route";
  const triggerPage = editLaserLinkItem?.guide_trigger_page ?? "";
  const [routeCheck, setRouteCheck] = useState(null);

  const update = (patch) => updateEditLaserLinkItem({ ...editLaserLinkItem, ...patch });
  const updateOptIn = (patch) => update({ opt_in: { ...optIn, ...patch } });

  useEffect(() => {
    if (variant !== "guide" || !editLaserLinkItem?.doc_id) return;
    const language = editLaserLinkItem?.language ? `&language=${encodeURIComponent(editLaserLinkItem.language)}` : "";
    networkService(`personal_insights_guides/coverage.json?doc_id=${editLaserLinkItem.doc_id}${language}`, "GET")
      .then(({ data }) => setCoverage(data || null))
      .catch(() => setCoverage(null));
  }, [variant, editLaserLinkItem?.doc_id, editLaserLinkItem?.language]);

  useEffect(() => {
    if (variant !== "guide" || !editLaserLinkItem?.doc_id) return undefined;
    const query = new URLSearchParams({
      doc_id: editLaserLinkItem.doc_id,
      language: editLaserLinkItem?.language || "",
      landing_page: editLaserLinkItem?.landing_page || "",
      disable_scroll: editLaserLinkItem?.disable_scroll ? "true" : "false",
      disable_on: editLaserLinkItem?.disable_settings?.disable_on || "",
      page: trigger === "page" ? triggerPage : "",
    });
    const timer = setTimeout(() => {
      networkService(`personal_insights_guides/route_check.json?${query.toString()}`, "GET")
        .then(({ data }) => setRouteCheck(data || null))
        .catch(() => setRouteCheck(null));
    }, 400);
    return () => clearTimeout(timer);
  }, [
    variant,
    trigger,
    triggerPage,
    editLaserLinkItem?.doc_id,
    editLaserLinkItem?.language,
    editLaserLinkItem?.landing_page,
    editLaserLinkItem?.disable_scroll,
    editLaserLinkItem?.disable_settings?.disable_on,
  ]);

  useEffect(() => {
    if (!hostedDocument?.id || hostedDocument.status !== "quarantined") return undefined;
    let cancelled = false;
    let timer = null;
    const check = async () => {
      try {
        const response = await fetch(`/hosted_documents/${hostedDocument.id}.json`, {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        });
        const data = response.ok ? await response.json() : null;
        if (!cancelled && data?.status && data.status !== "quarantined") {
          updateEditLaserLinkItem((current) => ({ ...current, hosted_document: data }));
          return;
        }
      } catch (error) {
        timer = null;
      }
      if (!cancelled) timer = setTimeout(check, 4000);
    };
    timer = setTimeout(check, 3000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [hostedDocument?.id, hostedDocument?.status]);

  const optinMode =
    optIn.optin_mode || (editLaserLinkItem?.optin_2nd_page_viewed ? OPTIN_MODES.SECOND_PAGE : OPTIN_MODES.PAGE_NUMBER);
  const afterPages = Number(optIn.optin_after_pages) >= 1 ? Number(optIn.optin_after_pages) : DEFAULT_OPTIN_AFTER_PAGES;
  const viewedPage = optinMode === OPTIN_MODES.PAGES_VIEWED ? afterPages + 1 : "legacy";
  const legacyLabel =
    optinMode === OPTIN_MODES.SECOND_PAGE
      ? "Page 3 of the document (current setting)"
      : `Page ${optIn.show_on_page || "?"} of the document (current setting)`;
  const showDelay = optIn.show_delay ?? 0;
  const delaySeconds = Number(showDelay) || 0;

  const appearsOnHint = () => {
    if (viewedPage === "legacy") {
      return "This LaserLINK still uses an older page setting. Pick a viewed page to switch to the new behaviour.";
    }
    const ordinal = ORDINALS[viewedPage] || `${viewedPage}th`;
    return delaySeconds > 0
      ? `Shows ${delaySeconds} ${delaySeconds === 1 ? "second" : "seconds"} after the recipient lands on their ${ordinal} viewed page.`
      : `Shows as soon as the recipient lands on their ${ordinal} viewed page.`;
  };

  const changeViewedPage = (value) => {
    if (value === "legacy") return;
    update({
      optin_2nd_page_viewed: false,
      opt_in: { ...optIn, optin_mode: OPTIN_MODES.PAGES_VIEWED, optin_after_pages: Number(value) - 1 },
    });
  };

  const translateGuideName = async () => {
    const name = (guideNames.en || "").trim();
    if (!name || name === translatedNameRef.current) return;
    translatedNameRef.current = name;
    setTranslatingName(true);
    try {
      const { data } = await networkService("personal_insights_guides/translate_name.json", "POST", { name });
      const names = data?.names || {};
      if (Object.keys(names).length === 0) return;
      updateEditLaserLinkItem((current) => {
        const currentNames = current?.recipient_guide_names || {};
        if ((currentNames.en || "").trim() !== name) return current;
        return { ...current, recipient_guide_names: { ...currentNames, ...names } };
      });
    } catch (error) {
      translatedNameRef.current = "";
    } finally {
      setTranslatingName(false);
    }
  };

  const resetGuideName = () => {
    translatedNameRef.current = "";
    update({ recipient_guide_names: {} });
  };

  const changeDelivers = (value) => {
    if (value === "guide") {
      update({ optin_delivers: delivers === "guide_review" ? "guide_review" : "guide_auto" });
    } else {
      update({ optin_delivers: value });
    }
  };

  const uploadDocument = async (file) => {
    if (!file) return;
    setUploadError("");
    if (file.size > 25 * 1024 * 1024) {
      setUploadError("The file is larger than 25 MB.");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("authenticity_token", csrfToken());
      formData.append("file", file);
      const response = await fetch("/hosted_documents.json", {
        method: "POST",
        body: formData,
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setUploadError(data?.error || "The file could not be uploaded.");
        return;
      }
      update({ hosted_document_id: data.id, hosted_document: data });
    } catch (error) {
      setUploadError("The file could not be uploaded.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const englishTiming = (key) => copy.delivery_timing?.[`${trigger === "page" ? "page_" : ""}${key}`] || "";
  const missingCoverage = coverage ? Object.keys(COVERAGE_HELP).filter((key) => !coverage[key]) : [];
  const otherLanguages = (copy.languages || []).filter((language) => language.code !== "en");
  const languages = copy.languages || [{ code: "en", name: "English" }];

  const localeCopy = copy.preview_locales?.[previewLocale] || {
    base: copy.base || {},
    variants: copy.variants || {},
    delivery_timing: copy.delivery_timing || {},
    guide_name_default: copy.guide_name_default,
    kdf_types: Object.fromEntries((copy.kdf_types || []).map((option) => [option.value, option.phrase])),
    deliverables: Object.fromEntries((copy.deliverables || []).map((option) => [option.value, option.phrase])),
    dir: "ltr",
  };
  const base = localeCopy.base || {};
  const variantCopy = localeCopy.variants?.[variant] || {};
  const previewValues = {
    kdf_type: localeCopy.kdf_types?.[editLaserLinkItem?.doc_kdf_type || "document"] || "this document",
    deliverable: localeCopy.deliverables?.[editLaserLinkItem?.deliverable || "document"] || "the document",
    recipient_guide_name:
      guideNames[previewLocale]?.trim() || localeCopy.guide_name_default || "Personal Insights",
    delivery_timing:
      localeCopy.delivery_timing?.[`${trigger === "page" ? "page_" : ""}${delivers === "guide_review" ? "review" : "automatic"}`],
  };
  const fill = (text) =>
    fillPreview(text).replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (match, key) => previewValues[key] || match);

  const asksPhone = !!optIn.ask_telephone_number;
  const phoneOptional = optIn.telephone_optional !== false;
  const allowSkip = !!optIn.allow_continue_without_sharing;

  const stepNumber = (number) => <span className="kz-gate__step-number">{number}</span>;

  return (
    <Row className="kz-gate">
      <Col lg={7} className="kz-gate__settings">
        <div className="kz-gate__enable">
          <Switch checked={optinEnabled} onChange={() => update({ has_optin: !optinEnabled })} />
          <div>
            <p className="kz-gate__enable-title">Enable opt-in</p>
            <p className="kz-gate__help mb-0">
              Recipients share their name and work email to continue. Used only for delivery and your follow-up, not
              marketing. Off: no opt-in and nothing is sent. On by default.
            </p>
          </div>
        </div>

        <fieldset disabled={!optinEnabled} className={`kz-gate__steps ${optinEnabled ? "" : "kz-gate__steps--off"}`}>
          <div className="kz-gate__step">
            {stepNumber(1)}
            <div className="kz-gate__step-body">
              <p className="kz-gate__label">Appears on</p>
              <p className="kz-gate__help">Recipients see the opt-in on this viewed page, after the delay.</p>
              <div className="kz-gate__inline">
                <select
                  className={`form-select kz-gate__page-select ${afterPagesError ? "is-invalid" : ""}`}
                  value={viewedPage}
                  onChange={(e) => changeViewedPage(e.target.value)}
                >
                  {viewedPage === "legacy" && <option value="legacy">{legacyLabel}</option>}
                  {VIEWED_PAGE_OPTIONS.map((page) => (
                    <option key={page} value={page}>
                      {ORDINALS[page]} viewed page{page === DEFAULT_OPTIN_AFTER_PAGES + 1 ? " (default)" : ""}
                    </option>
                  ))}
                </select>
                <span className="kz-gate__delay-group">
                <span className="kz-gate__help mb-0">after</span>
                <input
                  type="number"
                  min="0"
                  className="form-control kz-gate__delay"
                  value={showDelay}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || Number(value) >= 0) updateOptIn({ show_delay: value === "" ? "" : Number(value) });
                  }}
                  onBlur={(e) => {
                    if (e.target.value === "") updateOptIn({ show_delay: 0 });
                  }}
                />
                <span className="kz-gate__help mb-0">seconds</span>
                </span>
              </div>
              <p className="kz-gate__help mt-1">{appearsOnHint()}</p>
            </div>
          </div>

          <div className="kz-gate__step">
            {stepNumber(2)}
            <div className="kz-gate__step-body">
              <p className="kz-gate__label">Opt-in delivers</p>
              <p className="kz-gate__help">What the recipient gets for sharing their details. This picks the fixed opt-in wording.</p>
              <select className="form-select" value={variant} onChange={(e) => changeDelivers(e.target.value)}>
                {DELIVERS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <p className="kz-gate__help mt-1">{DELIVERS_OPTIONS.find((option) => option.value === variant)?.help}</p>
            </div>
          </div>

          <div className="kz-gate__step">
            {stepNumber(3)}
            <div className="kz-gate__step-body">
              {variant === "guide" && (
                <>
                  <p className="kz-gate__label">Guide name</p>
                  <p className="kz-gate__help">
                    Keep the default, or rename it to suit your content (for example "Buyer Brief"). It reads after
                    "your" and may be plural.{" "}
                    <button
                      type="button"
                      className="kz-gate__link"
                      onClick={() => {
                        if (!showAllNames) translateGuideName();
                        setShowAllNames((v) => !v);
                      }}
                    >
                      {showAllNames ? "Hide translations" : "Review translations"}
                    </button>
                  </p>
                  <div className="kz-gate__inline">
                    <Input
                      value={guideNames.en || ""}
                      placeholder={copy.guide_name_default || "Personal Insights"}
                      maxLength={60}
                      onChange={(e) => update({ recipient_guide_names: { ...guideNames, en: e.target.value } })}
                      onBlur={translateGuideName}
                    />
                    <button type="button" className="kz-gate__link" onClick={resetGuideName}>
                      Reset
                    </button>
                  </div>
                  {translatingName && <p className="kz-gate__help mt-1 mb-0">Translating the name into the other languages…</p>}
                  {showAllNames && (
                    <div className="mt-2">
                      <p className="kz-gate__help">
                        {guideNames.en?.trim()
                          ? "Translated automatically. Change any language if the translation doesn't fit."
                          : "Leave a language empty to use its translated default."}
                      </p>
                      {otherLanguages.map((language) => (
                        <div key={language.code} className="d-flex align-items-center gap-2 mb-1">
                          <span className="small" style={{ minWidth: 90 }}>{language.name}</span>
                          <Input
                            value={guideNames[language.code] || ""}
                            placeholder={copy.preview_locales?.[language.code]?.guide_name_default || "Translated default"}
                            maxLength={60}
                            dir="auto"
                            onChange={(e) =>
                              update({ recipient_guide_names: { ...guideNames, [language.code]: e.target.value } })
                            }
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  <Form.Check
                    type="checkbox"
                    id="kz-pi-review"
                    className="kz-gate__check mt-3"
                    checked={delivers === "guide_review"}
                    onChange={(e) => update({ optin_delivers: e.target.checked ? "guide_review" : "guide_auto" })}
                    label={
                      <>
                        <span className="kz-gate__label">Review before sending</span>
                        <span className="kz-gate__help d-block">
                          {delivers === "guide_review"
                            ? `You approve each guide in Personal Insights before it is sent. The opt-in says "${englishTiming("review")}".`
                            : `Guides go out automatically. The opt-in says "${englishTiming("automatic")}".`}
                        </span>
                      </>
                    }
                  />

                  <p className="kz-gate__label mt-3">Deliver the guide</p>
                  <div className="mb-1">
                    <Form.Check
                      type="radio"
                      id="kz-pi-trigger-end"
                      name="kz-pi-trigger"
                      className="laser-setting-popup"
                      checked={trigger === "end_of_route"}
                      onChange={() => update({ guide_trigger: "end_of_route" })}
                      label="At the end of their route"
                    />
                    <Form.Check
                      type="radio"
                      id="kz-pi-trigger-page"
                      name="kz-pi-trigger"
                      className="laser-setting-popup"
                      checked={trigger === "page"}
                      onChange={() =>
                        update({ guide_trigger: "page", guide_trigger_page: editLaserLinkItem?.guide_trigger_page || totalPages || 1 })
                      }
                      label={
                        <span className="cursor-pointer">
                          When they land on page&nbsp;
                          <input
                            type="number"
                            min={1}
                            max={totalPages || undefined}
                            className="edit-box-input"
                            value={triggerPage}
                            onChange={(e) =>
                              update({
                                guide_trigger: "page",
                                guide_trigger_page: e.target.value === "" ? "" : Number(e.target.value),
                              })
                            }
                          />
                          {totalPages ? ` / ${totalPages}` : ""}
                        </span>
                      }
                    />
                  </div>
                  {triggerPageError && trigger === "page" && hasValue(editLaserLinkItem?.guide_trigger_page) && (
                    <small className="text-danger d-block mb-1">Page must be between 1 and {totalPages || 1}.</small>
                  )}
                  <p className="kz-gate__help">
                    {trigger === "page"
                      ? `The guide is sent when the recipient lands on page ${triggerPage || "N"}, about 2 seconds after they arrive, or at the end of their route if they get there first.`
                      : "The guide is sent about 2 seconds after the recipient reaches the last page of their route."}{" "}
                    If that page asks a question, we wait up to 3 minutes for the answer. Recipients who reach neither get
                    their guide 30 minutes after their last activity.
                  </p>
                  {trigger === "end_of_route" && routeCheck?.scrolling_enabled && (
                    <div className="alert alert-warning small py-2">
                      Scrolling is enabled on this LaserLINK, so recipients can scroll freely and may never reach the end of a
                      route. Delivering when they land on a page is more reliable here.
                    </div>
                  )}
                  {trigger === "page" && !triggerPageError && routeCheck?.cyoa && routeCheck?.reachable === false && (
                    <div className="alert alert-warning small py-2">
                      No route from the landing page reaches page {triggerPage}. Recipients will get their guide at the end
                      of their route instead.
                    </div>
                  )}
                  {trigger === "page" && !triggerPageError && routeCheck?.cyoa && routeCheck?.reachable && routeCheck?.skippable && (
                    <div className="alert alert-warning small py-2">
                      Some routes reach an end without passing page {triggerPage}. Those recipients will get their guide at
                      the end of their route instead.
                    </div>
                  )}

                  <p className="kz-gate__label mt-3">
                    Guidance for the guide{" "}
                    <CustomTooltip
                      placement="right"
                      title={
                        <ul className="kz-gate__tips">
                          {GUIDANCE_TIPS.map((tip) => <li key={tip}>{tip}</li>)}
                        </ul>
                      }
                    >
                      <span className="kz-gate__info" tabIndex={0} aria-label="Tips for the guidance">
                        <FontAwesomeIcon icon={faCircleInfo} />
                      </span>
                    </CustomTooltip>
                  </p>
                  <p className="kz-gate__help">
                    What the AI should emphasise or avoid, such as claims, pricing, competitors and tone. Optional. Don't
                    include personal details.
                  </p>
                  <textarea
                    className="form-control"
                    rows={3}
                    maxLength={4000}
                    value={editLaserLinkItem?.guide_guidance || ""}
                    placeholder="Example: Readers are evaluators who must convince a finance lead. Do not quote prices or promise delivery dates."
                    onChange={(e) => update({ guide_guidance: e.target.value })}
                  />

                  <p className="kz-gate__label mt-3">Question coverage</p>
                  <p className="kz-gate__help">Read-only check of this KDF's questions. The guide is fuller when all four are covered.</p>
                  {coverage == null ? (
                    <p className="kz-gate__help">Checking the KDF's questions…</p>
                  ) : (
                    <>
                      <div className="kz-gate__chips">
                        {Object.entries(COVERAGE_HELP).map(([key, help]) => (
                          <span key={key} className={`kz-gate__chip ${coverage[key] ? "" : "kz-gate__chip--missing"}`}>
                            {coverage[key] ? "✓" : "•"} {help.label}: {coverage[key] ? "covered" : "missing"}
                          </span>
                        ))}
                      </div>
                      {missingCoverage.length > 0 && (
                        <p className="kz-gate__help mt-1">
                          {joinWithAnd(missingCoverage.map((key) => COVERAGE_HELP[key].label))}{" "}
                          {missingCoverage.length === 1 ? "is" : "are"} not covered. Add a question such as{" "}
                          {joinWithAnd(missingCoverage.map((key) => `"${COVERAGE_HELP[key].example}"`))} and tag it in its
                          Navigator settings. You can still publish.
                        </p>
                      )}
                    </>
                  )}
                </>
              )}

              {variant === "document" && (
                <>
                  <div className="kz-pi-field">
                    <span className="kz-gate__label">PDF file</span>
                    <input
                      ref={fileInputRef}
                      id="kz-pi-file-input"
                      type="file"
                      accept="application/pdf,.pdf"
                      className="kz-pi-file__input"
                      disabled={uploading || !optinEnabled}
                      onChange={(e) => uploadDocument(e.target.files?.[0])}
                    />
                    {hostedDocument && !uploading ? (
                      <div className={`kz-pi-file kz-pi-file--${hostedDocument.status}`}>
                        <div className="kz-pi-file__icon" aria-hidden="true">PDF</div>
                        <div className="kz-pi-file__body">
                          <span className="kz-pi-file__name">{hostedDocument.original_filename}</span>
                          <span className="kz-pi-file__meta">
                            {formatSize(hostedDocument.byte_size)}
                            <span className={`kz-pi-file__pill kz-pi-file__pill--${hostedDocument.status}`}>
                              {DOCUMENT_STATUS[hostedDocument.status] || hostedDocument.status}
                            </span>
                          </span>
                          {hostedDocument.rejection_reason && (
                            <span className="kz-pi-file__reason">{hostedDocument.rejection_reason}</span>
                          )}
                        </div>
                        <label htmlFor="kz-pi-file-input" className="kz-pi-file__button">Replace PDF</label>
                      </div>
                    ) : (
                      <div className="kz-pi-file kz-pi-file--empty">
                        <div className="kz-pi-file__icon" aria-hidden="true">PDF</div>
                        <div className="kz-pi-file__body">
                          <span className="kz-pi-file__name">{uploading ? "Uploading…" : "Upload a PDF"}</span>
                          <span className="kz-pi-file__meta">PDF only, up to 25 MB, no password. Checked for safety before use.</span>
                        </div>
                        {!uploading && (
                          <label htmlFor="kz-pi-file-input" className="kz-pi-file__button kz-pi-file__button--primary">Choose file</label>
                        )}
                      </div>
                    )}
                    {uploadError && <span className="kz-pi-file__error">{uploadError}</span>}
                    {documentError && !uploading && !uploadError && (
                      <span className="kz-pi-file__error">
                        {hostedDocument
                          ? "This PDF can't be sent. Upload a different PDF before saving."
                          : "Upload the PDF to send before saving."}
                      </span>
                    )}
                  </div>

                  <div className="kz-pi-field">
                    <label htmlFor="kz-pi-deliverable" className="kz-gate__label">Call it</label>
                    <span className="kz-gate__help">How the opt-in refers to your PDF.</span>
                    <select
                      id="kz-pi-deliverable"
                      className="form-select kz-gate__call-it"
                      value={editLaserLinkItem?.deliverable || "document"}
                      onChange={(e) => update({ deliverable: e.target.value })}
                    >
                      {(copy.deliverables || []).map((option) => (
                        <option key={option.value} value={option.value}>{option.phrase}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {variant === "reference" && (
                <div className="kz-gate__note">
                  <p className="kz-gate__label mb-1">Nothing more to set</p>
                  <p className="kz-gate__help mb-0">
                    The recipient gets a reference number by email, and it appears on your dashboard record so you can
                    match the two.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="kz-gate__locked">
            <FontAwesomeIcon icon={faLock} className="kz-gate__locked-icon" />
            <p className="mb-0">
              <strong>The opt-in wording is fixed.</strong>{" "}
              <span className="kz-gate__help">
                Kinetizine writes it and translates it into 10 languages, so it stays accurate and consistent. You control
                the settings above.
              </span>
            </p>
          </div>
        </fieldset>

        <div className="kz-gate__more">
          <button
            type="button"
            className="kz-gate__more-toggle"
            aria-expanded={showMoreOptions}
            onClick={() => setShowMoreOptions((v) => !v)}
          >
            <FontAwesomeIcon icon={showMoreOptions ? faChevronDown : faChevronRight} /> More options
          </button>
          {showMoreOptions && (
            <div className="kz-gate__more-body">
              <p className="kz-gate__section">Who can open this link</p>
              {accessControls}

              <p className="kz-gate__section mt-3">Opt-in extras</p>
              <fieldset disabled={!optinEnabled} className={optinEnabled ? "" : "kz-gate__steps--off"}>
                <Form.Check
                  type="checkbox"
                  id="kz-pi-skip"
                  className="kz-gate__check"
                  checked={allowSkip}
                  onChange={() => updateOptIn({ allow_continue_without_sharing: !allowSkip })}
                  label={
                    <>
                      <span className="kz-gate__label">Allow "Continue without sharing"</span>
                      <span className="kz-gate__help d-block">
                        Off by default. A recipient who skips gets no email, reference number or deliverable. Switch it
                        on if you rely on consent, or if you collect more than the deliverable needs.
                      </span>
                    </>
                  }
                />

                <p className="kz-gate__label mt-3">Reply-To address</p>
                <p className="kz-gate__help">Where recipient replies go. Defaults to the link owner.</p>
                <Input
                  type="email"
                  value={editLaserLinkItem?.optin_reply_to || ""}
                  placeholder={ownerEmail || "Your account email"}
                  onChange={(e) => update({ optin_reply_to: e.target.value })}
                />

                <Form.Check
                  type="checkbox"
                  id="kz-pi-phone"
                  className="kz-gate__check mt-3"
                  checked={asksPhone}
                  onChange={() => updateOptIn({ ask_telephone_number: !asksPhone })}
                  label={
                    <>
                      <span className="kz-gate__label">Also ask for a work phone number</span>
                      <span className="kz-gate__help d-block">Off by default. Adds a Work phone field.</span>
                    </>
                  }
                />
                {asksPhone && (
                  <Form.Check
                    type="checkbox"
                    id="kz-pi-phone-optional"
                    className="kz-gate__check kz-gate__check--nested"
                    checked={phoneOptional}
                    onChange={() => updateOptIn({ telephone_optional: !phoneOptional })}
                    label={
                      <>
                        <span className="kz-gate__label">Phone number is optional</span>
                        <span className="kz-gate__help d-block">
                          Ticked: recipients can leave it blank. Unticked: required to continue.
                        </span>
                      </>
                    }
                  />
                )}

              </fieldset>
            </div>
          )}
        </div>
      </Col>

      <Col lg={5}>
        <div className="kz-gate__preview-panel">
          <p className="kz-gate__section">What recipients see</p>
          <label htmlFor="kz-pi-preview-language" className="kz-gate__help mb-1">Preview language</label>
          <select
            id="kz-pi-preview-language"
            className="form-select mb-3"
            value={previewLocale}
            onChange={(e) => setPreviewLocale(e.target.value)}
          >
            {languages.map((language) => (
              <option key={language.code} value={language.code}>{language.name}</option>
            ))}
          </select>
          <div className={`kz-optin-modal optSettingsModal kz-gate__popup ${optinEnabled ? "" : "kz-gate__steps--off"}`}>
            <div className="modal-content">
              <div className="modal-header" />
              <div className="modal-body">
                <div className="kz-optin" dir={localeCopy.dir || "ltr"} lang={previewLocale}>
                  <div className="kz-language-corner">
                    <LanguageSelect
                      languages={languages}
                      value={previewLocale}
                      onChange={setPreviewLocale}
                      label={localeCopy.language_label}
                    />
                  </div>
                  <p className="kz-optin__benefit">{fill(variantCopy.intro)}</p>
                  <div className="kz-optin__field">
                    <Input readOnly placeholder={base.first_name || "First name"} className="fs-6" />
                  </div>
                  <div className="kz-optin__field">
                    <Input readOnly placeholder={base.last_name || "Last name"} className="fs-6" />
                  </div>
                  <div className="kz-optin__field">
                    <Input readOnly placeholder={base.email || "Work email"} className="fs-6" />
                  </div>
                  {asksPhone && (
                    <div className="kz-optin__field">
                      <Input
                        readOnly
                        placeholder={phoneOptional ? base.phone_optional || "Work phone (optional)" : base.phone || "Work phone"}
                        className="fs-6"
                      />
                    </div>
                  )}
                  <div className="kz-optin__actions">
                    <Button text={base.continue || "Continue"} classList="success" addonClass="kz-optin__continue" />
                  </div>
                  {variantCopy.after_button && <p className="kz-optin__disclosure">{fill(variantCopy.after_button)}</p>}
                  {allowSkip && (
                    <div className="kz-optin__actions">
                      <button type="button" className="kz-optin__skip">{localeCopy.skip || "Continue without sharing"}</button>
                    </div>
                  )}
                  <p className="kz-optin__footer">
                    {fill(base.locked_line)}{" "}
                    <a href={resolvePrivacyUrl(profile?.privacy_link)} target="_blank" rel="noopener noreferrer">
                      {base.privacy_notice || "Privacy Notice"}
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <p className="kz-gate__help mt-2">
            Preview uses your name and company. Recipients see their browser language, or English if it is not one of the
            11. Translations are drafts pending review by certified translators; fixed text is never editable by senders.
          </p>
        </div>
      </Col>
    </Row>
  );
};

export default PersonalInsightsOptinSettings;
